import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { ensureEndsWith, HTTPStatusCodes, isNullOrWhitespace } from '@asamco/utils';
import { AuthenticatedResult, OidcSecurityService } from 'angular-auth-oidc-client';
import { del, get, set } from 'idb-keyval';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, combineLatest, concat, firstValueFrom, from, Observable, of, onErrorResumeNext, ReplaySubject, Subject, throwError } from 'rxjs';
import {
	catchError,
	concatMap,
	delay,
	distinctUntilChanged,
	map,
	mergeAll,
	mergeMap,
	shareReplay,
	startWith,
	switchMap,
	take,
	tap,
	timeout,
	toArray,
} from 'rxjs/operators';

import { AppSettings } from '../configuration/appsettings';
import { BackEndApiType } from '../configuration/backend-api-type';
import { IServer } from '../configuration/iserver.interface';
import { ServerSetting } from '../configuration/server-setting.interface';
import { MessageBusService, MessageBusType } from '../message-bus/message-bus.service';
import {
	AdditionalLoginProvider,
	AdditionalLoginResponse,
	AdditionalLoginResult,
	AuthenticationProvider,
	AuthenticationResult,
	LoginCredentials,
	LoginResult,
	SelectedServer,
	UserSession,
} from './authentication.interfaces';
import { AzureB2CAuthenticationProvider } from './azure-b2c-login';
import { EpoAuthenticationProvider } from './epo-authentication-provider';
import { LoginStatus } from './login-status.enum';
import { POST_LOGIN_PROCESSOR, LATE_LOGIN_PROCESSOR, PostLoginProcessor } from './post-login-processor.interface';
import { POST_LOGOUT_PROCESSOR, PostLogoutProcessor } from './post-logout-processor.interface';
import { ServerProviderService } from './server-provider.service';
import { WebApiAuthenticationProvider } from './webapi-authentication-provider';
import { UserStatusLoginError } from '../errors/user-status-login-error';

export const sessionDataStorageKey = 'userSession';
export const serverSelectionData = 'AppShellServer';

/**
 * Authentication service
 */
@Injectable({
	providedIn: 'root',
	deps: [
		HttpClient,
		AppSettings,
		MessageBusService,
		Router,
		ServerProviderService,
		WebApiAuthenticationProvider,
		AzureB2CAuthenticationProvider,
		EpoAuthenticationProvider,
		OidcSecurityService,
		POST_LOGIN_PROCESSOR,
		POST_LOGOUT_PROCESSOR,
	],
})
export class AsamcoAuthService {
	/** user data */
	public userSession: UserSession;

	/** alternative backend for reporting calls */
	public reportingBackend: ServerSetting;

	/** subject to buffer internal state of login */
	private _isLoggedIn$ = new Subject<boolean>();

	/** exported observable  */
	isLoggedIn$ = this._isLoggedIn$.pipe(
		startWith(false),
		distinctUntilChanged(),
		tap(state => {
			this.msgBus.emit({
				type: MessageBusType.LoginState,
				payload: state,
			});
			this.msgBus.emit({ type: MessageBusType.EPO, subType: 'clearCache', payload: true });
		}),
		shareReplay(1)
	);

	userSession$ = new ReplaySubject<UserSession>();

	authProviders: AuthenticationProvider[];

	additionalAuthProviders: AdditionalLoginProvider[];
	sessionDataStorageKey: string;
	applicationId: string;

	// Only b2c for now
	authTokenChanged$: Observable<{ isAuthenticated: AuthenticatedResult; token: string }> = this.oidc.isAuthenticated$.pipe(
		switchMap(isAuthenticated => {
			return this.oidc.getAccessToken().pipe(map(at => ({isAuthenticated: isAuthenticated, token: at})))
		})
	);

	private userScope = new BehaviorSubject<{ [key: string]: number }>({});
	userScope$ = this.userScope.asObservable();

	async getPrimaryTokenAsync() {
		if (this.app.settings.b2c && this.app.settings.b2c.enabled) {
			return await firstValueFrom(this.oidc.getAccessToken());
		} else {
			return this.userSession.backend.token;
		}
	}

	/** Status of login flow */
	private _loginStatus: BehaviorSubject<LoginStatus> = new BehaviorSubject<LoginStatus>(LoginStatus.idle);

	/** Accessible login status as observable */
	public loginStatus = this._loginStatus.asObservable();

	/**
	 * Constructor
	 * note: the licenceService is not used, but I included it here to make sure its instantiated BEFORE auth changes can occur.
	 */
	constructor(
		private http: HttpClient,
		private app: AppSettings,
		private msgBus: MessageBusService,
		private router: Router,
		private sp: ServerProviderService,
		wa: WebApiAuthenticationProvider,
		b2c: AzureB2CAuthenticationProvider,
		epoAuth: EpoAuthenticationProvider,
		private oidc: OidcSecurityService,
		@Optional() @Inject(POST_LOGIN_PROCESSOR) private postLoginProcessors: PostLoginProcessor[],
		@Optional() @Inject(LATE_LOGIN_PROCESSOR) private lateLoginProcessors: PostLoginProcessor[],
		@Optional() @Inject(POST_LOGOUT_PROCESSOR) private postLogoutProcessors: PostLogoutProcessor[]
	) {
		this.sessionDataStorageKey = `${this.app.settings.appId}_${this.app.settings.userSessionStorageKey}`;

		this.dxStoreBeforeSend = this.dxStoreBeforeSend.bind(this);
		this.login = this.login.bind(this);

		if (app.settings.b2c?.enabled) {
			this.authProviders = [b2c];
		} else {
			this.authProviders = [wa];
		}

		this.authProviders.forEach(provider => {
			provider.initialize();
		});

		this.additionalAuthProviders = [epoAuth];
	}

	/**
	 * Checks which login procedure should be used for the selected api
	 */
	public tryRestoreLastSession(): Observable<LoginResult> {
		const onLoginError = () => {
			this.clearSessionData();
			this.redirectToLogin();
		};

		this._loginStatus.next(LoginStatus.inProgress);

		return from(get<UserSession>(this.sessionDataStorageKey)).pipe(
			catchError(_ => undefined),
			map((sd: string) => {
				if (isNullOrWhitespace(sd)) {
					return {};
				} else {
					return JSON.parse(sd);
				}
			}),
			concatMap((sessionData: UserSession) => {
				const supportedAuthProvider: AuthenticationProvider = this.authProviders[0];

				if (supportedAuthProvider == null) {
					onLoginError();
				}

				return supportedAuthProvider.getIsAuthorized(sessionData).pipe(
					concatMap(r => {
						if (r === false) {
							this._loginStatus.next(LoginStatus.idle);
							// TODO: This "error" looks like (and used to be cast to) an
							// LoginResult; should we throw or just build an invalid/failed LoginResult?
							return throwError(() => new Error(JSON.stringify({
								success: false,
								message: 'User was not authorized',
								invalidSessionData: true,
								loginStatusCode: HTTPStatusCodes.BAD_REQUEST,
							})));
						} else {
							return supportedAuthProvider.restorePreviousSession(sessionData);
						}
					})
				);
			}),
			concatMap(authResult => {
				return this.handleAuthenticationResult(authResult);
			})
		);
	}

	/**
	 * Redirect to login, check to prevent recusing.
	 */
	redirectToLogin() {
		if (!window.location.href.includes('/auth/login')) {
			// this setTimeout is needed to slow it down a bit in production.
			// This prevents an endless loop (in production)
			window.location.href = window.location.href.split('/#!')[0] + '/#!/auth/login';
			setTimeout(() => location.reload(), 200);
		}
	}

	/** Gets the last selected server */
	getLastSelectedServer(): Promise<SelectedServer> {
		return get<SelectedServer>(serverSelectionData);
	}

	/**
	 * Stores the last server used for login
	 * @param server
	 */
	storeLastSelectedServer(server: SelectedServer) {
		const data = {
			id: server.id,
			name: server.name,
			address: server.address,
			apiType: server.apiType,
			databaseId: server.databaseId,
		};

		set(serverSelectionData, data);
	}

	/**
	 * Starts login procedure to AD
	 */
	public loginWithAD() {
		const authProvider = this.authProviders[0];

		if (authProvider.backendType === BackEndApiType.B2C) {
			this._loginStatus.next(LoginStatus.inProgress);
			authProvider.login().subscribe({error: console.error});
		} else {
			console.error('B2C auth provider not set up');
		}
	}

	/**
	 * Starts the authentication flow
	 */
	login(
		username,
		password,
		{ serverId, databaseId } = {
			serverId: undefined,
			databaseId: undefined,
		},
		kickOther = false
	) {
		// Stop if no username or password was entered
		if (isNullOrWhitespace(username) || isNullOrWhitespace(password)) {
			return of(false);
		}

		return concat(this.sp.databaseServers$, from(this.getLastSelectedServer())).pipe(
			toArray(),
			concatMap(([servers, ls]: [IServer[], SelectedServer]) => {
				// Use the localstorage information as alternative
				serverId = serverId || ls?.id;
				databaseId = databaseId || ls?.databaseId;
				let activeServer = servers.find(s => s.id === serverId);

				if (activeServer == null) {
					if (servers.length === 1) {
						activeServer = servers[0];
					} else if (servers.length <= 0) {
						throwError(() => { message: 'No available servers, please contact an administrator if issue persists.' });
					} else {
						throwError(() => { message: 'Multiple possible servers, please pick one from the list' });
					}
				}

				// Select the db from the last session, or if only 1 is available, select that database
				const database = activeServer.databases.find(
					db => (activeServer.databases.length === 1 && databaseId == null) || db.id === databaseId
				);

				databaseId = database.id;
				const externalDatabaseId = database.externalId;

				// store the primary login server to local/indexedDB storage.
				this.storeLastSelectedServer({
					address: activeServer.address,
					apiType: activeServer.apiType,
					id: activeServer.id,
					name: activeServer.name,
					databaseId: databaseId,
				});

				const creds: LoginCredentials = {
					activeServer,
					databaseId,
					username,
					password,
					kickOther: !!kickOther,
					databaseExternalId: externalDatabaseId,
				};
				// hand off data to next operator
				return of(creds);
			}),
			catchError(() => {
				this._isLoggedIn$.next(false);
				return throwError(() => <LoginResult>{
					message: 'Please check your entry, server and database settings.',
					loginStatusCode: HTTPStatusCodes.INTERNAL_SERVER_ERROR,
				});
			}),
			this.doLogin(),
			concatMap(authResult => this.handleAuthenticationResult(authResult))
		);
	}

	async storeSession(session: UserSession) {
		await set(this.sessionDataStorageKey, JSON.stringify(session));
	}

	/**
	 * Parse the additional login responses from the backend and add the server name
	 * @param token
	 */
	async getAdditionalLoginResponses(token: string): Promise<AdditionalLoginResponse[]> {
		// Get the servers so we can store the name
		const servers = await firstValueFrom(this.sp.serverList$).catch(() => [] as IServer[]);

		// Decode the token
		const decoded = <any>jwtDecode(token);
		const additionalLogins: AdditionalLoginResponse[] = (decoded.additionalLogins && JSON.parse(decoded.additionalLogins)) || [];

		/** merge in the addresses from the server list */
		const combined = additionalLogins.map((l: any) => {
			const server: any = servers.find(row => row.id === l.serverId) || {};
			return {
				name: server.name,
				...l,
			};
		});

		return combined;
	}

	private handleAdditionalLogins(userSession: UserSession) {
		return from(this.getAdditionalLoginResponses(userSession.backend.token)).pipe(
			tap(additionalLoginResponses => {
				// Add all backends the user is logged into to the user session
				const additionalBackends = additionalLoginResponses.map(alr => {
					return { ...this.app.settings.serverSettings['' + alr.apiType], baseUrl: alr.serverUrl, token: alr.token };
				});

				userSession.loggedInBackends.push(...additionalBackends);
			}),
			mergeMap((additionalLoginResponses: AdditionalLoginResponse[]) => {
				// Request the userdata for the additional logins
				return additionalLoginResponses.map(alr => {
					const authProvider = this.additionalAuthProviders.find(ap => ap.backendType === alr.apiType);

					if (authProvider == null) {
						throw new Error(`Additional login is not supported (anymore). ${alr.name} | ${alr.serverUrl}`);
					}

					authProvider.beforeLogin(userSession, alr);

					return authProvider.getUserData(alr);
				});
			}),
			mergeAll(),
			toArray(),
			map((alResults: AdditionalLoginResult[]) => {
				// Add userdata to the session
				alResults.forEach(alr => {
					if (!alr.success) {
						throw new Error(`Additional login failed, reason: ${alr.message}`);
					}

					if (alr.type === BackEndApiType.Evolution) {
						userSession.evolutionSession = alr.user;
					}
				});

				return userSession;
			})
		);
	}

	/**
	 * Login with the primary server and starts a new session
	 */
	private doLogin() {
		return concatMap((loginCreds: LoginCredentials) => {
			// Get the auth provider
			const authProvider = this.authProviders.find(ap => ap.backendType === loginCreds.activeServer.apiType);

			// Stop if the auth provider doesn't exist (anymore)
			if (authProvider == null) {
				const ex = `Authentication method is not supported. ${loginCreds.activeServer.name} | ${loginCreds.activeServer.address}`;
				throw new Error(ex);
			}

			return authProvider.login(loginCreds);
		});
	}

	private handleAuthenticationResult(loginResult: AuthenticationResult): Observable<LoginResult> {
		if (loginResult.success === false) {
			this._isLoggedIn$.next(false);
			this._loginStatus.next(LoginStatus.idle);
			return throwError(() => <LoginResult>{
				message: loginResult.message,
				loginStatusCode: loginResult.httpStatusCode,
			});
		}

		// Get primary server details
		let backendDetails: ServerSetting = {
			...this.app.settings.serverSettings[loginResult.authProvider.backendType],
		};

		// Handle auth provider specific things
		if (loginResult.authProvider.backendType === BackEndApiType.WebApi) {
			backendDetails = Object.assign(backendDetails, {
				baseUrl: ensureEndsWith(loginResult.server.address, '/'),
				serverId: loginResult.server.id,
				databaseId: loginResult.databaseId,
				odataBaseUrl: loginResult.server.address.replace(/\/api\/?$/, '/odata/'),
				customerId: loginResult.server.customerId,
				name: loginResult.server.name,
				token: loginResult.token,
			});
		} else if (loginResult.authProvider.backendType === BackEndApiType.B2C) {
			backendDetails = Object.assign(backendDetails, {
				baseUrl: ensureEndsWith(backendDetails.baseUrl, '/'),
				odataBaseUrl: backendDetails.baseUrl.replace(/\/api\/?$/, '/odata/'),
				token: loginResult.token,
				apiType: BackEndApiType.B2C,
			});

			if (jwtDecode(backendDetails.token).exp * 1000 < new Date().getTime()) {
				this._isLoggedIn$.next(false);
				this._loginStatus.next(LoginStatus.idle);
				return throwError(() => <LoginResult>{
					message: 'Token expired',
					loginStatusCode: HTTPStatusCodes.UNAUTHORIZED,
				});
			}
		}

		// Create session
		const session: UserSession = {
			backend: backendDetails,
			user: loginResult.user,
			loggedInBackends: [backendDetails],
		};

		return of(session).pipe(
			concatMap(us => this.handleAdditionalLogins(us)),
			catchError(err => of(false)),
			concatMap(result => {
				if (result === false) {
					this._isLoggedIn$.next(false);
					return throwError(() => <LoginResult>{
						message: loginResult.message,
						loginStatusCode: loginResult.httpStatusCode,
					});
				}

				return of(result as UserSession);
			}),
			// TODO AL3 / csp: We have removed the jwt nbf check. This should not be a problem as we implement a clock skew in the backend and adjused renewTimeBeforeTokenExpiresInSeconds appropriately.
			concatMap((us: UserSession) => {
				// Executes all post login processors, returning the (possibly updated) usersession.
				// If an error occurs it returns null to indicate something went wrong.
				// The concatMap filters the null and return a failed login result to make the login
				// flow fail.
				return this.postLoginProcessors
					.reduce((pv, cv) => pv.pipe(concatMap(processedUs => cv.execute(processedUs))), of(us))
					.pipe(
						catchError((error: any) => {
							this._isLoggedIn$.next(false);
							this._loginStatus.next(LoginStatus.idle);

							if (error instanceof UserStatusLoginError) {
								return throwError(() => error);
							}

							return throwError(() => <LoginResult>{
								message: error?.message ?? 'Post login processor failed',
								loginStatusCode: HTTPStatusCodes.BAD_REQUEST,
							});
						})
					);
			}),
			switchMap((us: UserSession) => {
				// Save the usersession after all the post login processors have been handled.
				this.userSession = us;
				this.userSession$.next(us);

				// late side effects of logging in. can be excecuted without awaiting. may add extra backend options for later use.
				this.lateLoginProcessors?.reduce((a, c) => a.pipe(switchMap(us => c.execute(us).pipe(catchError(() => of(us))))), of(this.userSession))
				.pipe(tap(us2 => from(this.storeSession(us2)))).subscribe(us2 => {
					this.userSession = us2;
					this.userSession$.next(us2);
				});

				// DONT (RE)MOVE THIS EXTRA 'TAP', it will break the login on refresh
				return from(this.storeSession(us)).pipe(
					tap(_ => {
						// Disabled this since it shows the login button while it is still redirecting
						// after login.
						// this._loginStatus.next(LoginStatus.idle);
						this._isLoggedIn$.next(true);
					}),
					map(_ => us)
				);
			}),
			map<UserSession, LoginResult>((us: UserSession) => {
				return {
					message: 'Loggin OK',
					loginStatusCode: HTTPStatusCodes.OK,
					userSession: us,
				};
			})
		);
	}

	private redirectToLastRoute() {
		const newRoute = this.app.settings.lastRouteBeforeLogin || this.app.settings.homePage || '/';

		if (this.app.settings.lastRouteBeforeLogin) {
			// reset global route.
			this.app.addOrUpdateSetting('lastRouteBeforeLogin', undefined);
		}

		this.router.navigate([newRoute]);
	}

	/**
	 * Logout the user in all back-end he's logged in
	 * returns a promise that's true if he is logged out.
	 */
	public async logOff(key?: string): Promise<boolean> {
		const beTypes = this.userSession.loggedInBackends.map(be => be.apiType);
		const authProvidersToLogOff = this.authProviders.filter(ap => beTypes.includes(ap.backendType));

		let logOff$: Observable<any>;

		if (authProvidersToLogOff.length === 0) {
			logOff$ = of(null);
		} else {
			logOff$ = onErrorResumeNext(authProvidersToLogOff.map(ap => ap.logoff(key).pipe(timeout(5000))));
		}

		return firstValueFrom(logOff$
			.pipe(
				catchError(_ => {
					this.redirectToLastRoute();
					return throwError(() => false);
				}),
				concatMap(_ => from(this.clearSessionData())),
				tap(_ => {
					if (Array.isArray(this.postLogoutProcessors)) {
						this.postLogoutProcessors.forEach(processor => {
							processor.execute(this.userSession);
						});
					}
				}),
				tap(_ => {

					/** do a hard refresh to flush everything out of memory. */
					window.location.reload();
				}),
				map(r => true)
			));
	}

	/** clear left over loginState from local storage/index db */
	async clearSessionData() {
		/** store an empty credential, flushes local-storage, and primary server login. */
		await del(this.sessionDataStorageKey);
		this.applicationId = null;
	}

	/**
	 * Add's authentication headers to the (odata) request
	 * @param request The request devextreme is going to execute
	 */
	public async dxStoreBeforeSend(request: any) {
		request.headers['Authorization'] = 'Bearer ' + (await this.getPrimaryTokenAsync());
		if (this.userScope.getValue() != null && Object.keys(this.userScope.getValue()).length) {
			request.headers['x-user-scope'] = JSON.stringify(this.userScope.getValue());
		}
	}

	/**
	 * Add's authentication headers to the (odata) request
	 * @param request The request devextreme is going to execute
	 */
	public async dxStoreBeforeSendHearderAuth() {
		return 'Bearer ' + (await this.getPrimaryTokenAsync());
	}

	/** Returns the main auth provider */
	public getMainAuthProver(): AuthenticationProvider {
		return this.authProviders && this.authProviders.length > 0 ? this.authProviders[0] : null;
	}

	/**
	 * Makes a call to the selected server that authenticates the switch
	 */
	public async authenticateSwitchServer({ serverId, databaseId }: { serverId: number; databaseId: number }): Promise<boolean> {
		const currentServerData = this.userSession.backend;
		const currentServerId = currentServerData.serverId;
		const currentDatabaseId = currentServerData.databaseId;

		if (serverId === currentServerId && databaseId === currentDatabaseId) return false; //nothing to do!

		const username = this.app.settings.user.userName;
		const activeServer = await firstValueFrom(this.sp.serverList$)
			.then(serverList => serverList.find(row => row.id === serverId));

		if (activeServer === undefined) {
			// uhOh, this shouldn't be possible
			throw new Error('new serverId unknown on authenticateSwitchServer');
		}

		var obs = from(this.getPrimaryTokenAsync()).pipe(
			switchMap(pt => this.http
				.post(activeServer.address + 'OauthAuthentication/SwitchServer', {
					username,
					serverFlag: serverId === currentServerId,
					currentDatabaseId,
					currentServerAddress: currentServerData.baseUrl,
					token: pt,
					selectedDatabaseId: databaseId,
				})),
				// store the new credentials, will return undefined if failed(no login.)
				switchMap(serverResult =>
					// only store on a valid result, otherwise current stored credentials wil be thrown away.
					serverResult === null || serverResult === '""' || serverResult === '' || serverResult === undefined
						? of(undefined)
						: this.replaceCredentials({
								activeServer,
								serverResult: serverResult,
								databaseId,
								username,
						  })
				),
				// In this case, we are only interested in the boolean result, the user is already stored by now
				map(r => (r ? true : false)),
				// just in case of any failure
				catchError(() => of(false)),
				tap(switchSuccess => {
					// ok, we are going to force an reload, to flush memory completely
					if (switchSuccess) {
						setTimeout(() => location.reload(), 150);
					}
				})
			);

		return firstValueFrom(obs);
	}

	async replaceCredentials({ activeServer, serverResult, databaseId, username }) {
		return null;
	}

	setUserScope(scope: string, objectId: number) {
		if (this.userScope.getValue() == null) {
			this.userScope.next({});
		}

		this.userScope.next({
			...this.userScope.getValue(),
			[scope]: objectId,
		});
	}

	getUserScopeValueObservable(scope: string, defaultIfNull: number = 0) {
		return this.userScope$.pipe(map(_ => this.getUserScopeValueOrDefault(scope, defaultIfNull)));
	}

	getUserScopes() {
		return this.userScope.getValue();
	}

	getUserScopeValueOrDefault(scope: string, defaultIfNull: number = 0) {
		const userScopes = this.userScope.getValue();
		if (userScopes?.[scope] != null) {
			return userScopes[scope];
		}

		return defaultIfNull;
	}
}
